/**
 * PostHog Analytics Configuration
 * 
 * SECURITY WARNING:
 * - NEVER commit API keys or sensitive data to version control
 * - Environment variables should be configured through Netlify's UI
 * - For local development, use .env.local (which is gitignored)
 */

import type { PostHog, Properties, CaptureResult, PostHogConfig } from 'posthog-js';

// Create a type-safe stub for PostHog methods
const stubPostHog: Partial<PostHog> = {
  capture: (_name: string, _properties?: Properties): CaptureResult | undefined => undefined,
  identify: () => {},
  reset: () => {},
  register: () => {},
  opt_in_capturing: () => {},
  opt_out_capturing: () => {},
  isFeatureEnabled: () => false,
  getFeatureFlag: () => undefined,
  reloadFeatureFlags: () => Promise.resolve(),
  init: (_token: string, _config?: Partial<PostHogConfig>): PostHog => stubPostHog as PostHog,
};

let posthog: Partial<PostHog> = { ...stubPostHog };

// PostHog configuration
export const initPostHog = async () => {
  console.log('Initializing PostHog...');
  console.log('Environment:', import.meta.env.MODE);
  console.log('PROD:', import.meta.env.PROD);
  
  // Respect Do Not Track and common content-blocker globals
  const dnt =
    (typeof navigator !== 'undefined' && (navigator as any).doNotTrack === '1') ||
    (typeof window !== 'undefined' && ((window as any).doNotTrack === '1' || (window as any).msDoNotTrack === '1'));
  if (dnt) {
    console.warn('DNT enabled - skipping PostHog initialization');
    return;
  }

  // Environment variables should be configured in Netlify for production
  // and in .env.local (gitignored) for development
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY || '';
  const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';
  
  // Only log if key exists, not its value
  console.log('PostHog Configuration Status:', {
    hasApiKey: !!apiKey,
    apiHost,
    environment: import.meta.env.MODE
  });
  
  if (!apiKey) {
    console.warn('PostHog API key is missing. Analytics will be disabled.');
    return;
  }
  
  try {
    // Dynamically import PostHog
    console.log('Importing PostHog...');
    const PostHogModule = await import('posthog-js');
    
    if (!PostHogModule.default || typeof PostHogModule.default.init !== 'function') {
      throw new Error('PostHog module loaded incorrectly. Missing init function.');
    }
    
    // Make sure we're using a clean string for the API key
    const sanitizedApiKey = String(apiKey).trim();
    
    if (!sanitizedApiKey || sanitizedApiKey.includes('$') || sanitizedApiKey.includes('{')) {
      throw new Error('Invalid PostHog API key format');
    }
    
    // Initialize PostHog with proper typing
    PostHogModule.default.init(sanitizedApiKey, {
      api_host: apiHost,
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      persistence: 'localStorage',
      debug: import.meta.env.MODE === 'development',
      loaded: (ph: PostHog) => {
        console.log('PostHog loaded successfully');
        if (!import.meta.env.PROD && import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') {
          console.log('Opting out of capturing in development');
          ph.opt_out_capturing();
        } else {
          console.log('Analytics enabled');
        }
      },
    });
    
    // Update the posthog instance with the initialized one
    posthog = PostHogModule.default;
    
    // Test event to verify PostHog is working
    posthog.capture?.('test_event', { test: true });
    
  } catch (error) {
    console.error('Failed to load PostHog:', error);
    // Reset to stub implementation if initialization fails
    posthog = { ...stubPostHog };
  }
};

export default posthog; 